import React, { useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";
import { useQuote } from "@/components/QuoteProvider";
import {
  ArrowRight, CheckCircle2, ShieldCheck, Award, Wrench, Truck, HeadphonesIcon,
  Package, Factory, Building2, Zap, Train, Plane, Cog, Anchor, Ship, Hammer,
  Droplets, Sun, Wind, Mountain, Layers, Circle, ChevronRight, Eye,
  Download, X, ZoomIn, ZoomOut, MessageCircle, Phone, Handshake,
} from "lucide-react";

const WHY = [
  { icon: Award, title: "Official Authorised Distributor", body: "Authorised by 3AK Chemie Pvt. Ltd. for MR Chemie NDT products in India." },
  { icon: ShieldCheck, title: "Genuine Products", body: "Only genuine MR Chemie, KLYDE and branded industrial products." },
  { icon: HeadphonesIcon, title: "Technical Support", body: "Product-selection guidance and application assistance from our technical team." },
  { icon: Wrench, title: "Fast Quotation Response", body: "Prompt, professional quotations for standard and custom industrial requirements." },
  { icon: Truck, title: "Consistent Supply", body: "Bulk industrial supply with GST billing across Telangana & Andhra Pradesh." },
  { icon: Handshake, title: "Trusted Industrial Partner", body: "A supplier your engineering, procurement and maintenance teams can rely on." },
];

const PRODUCTS = [
  {
    icon: Droplets, accent: "#C8102E",
    title: "MR Chemie NDT Products",
    body: "Dye Penetrant Testing, Magnetic Particle Inspection, UV inspection equipment and reference blocks.",
    to: "/products/dye-penetrant-testing",
  },
  {
    icon: Layers, accent: "#0F2747",
    title: "KLYDE Cold Galvanizing Zinc",
    body: "ZINCKED range of cold galvanizing sprays, zinc primers, zinc coatings and brushable zinc systems.",
    to: "/products/klyde-zinc-materials",
  },
  {
    icon: Wrench, accent: "#B5121B",
    title: "BESSEY Clamping & Cutting Tools",
    body: "Premium German clamping and cutting tools for fabrication, welding, metalworking, woodworking and industrial applications.",
    to: "/products/bessey-clamps",
  },
  {
    icon: Circle, accent: "#1E293B",
    title: "Industrial Bearings",
    body: "Ball, roller, taper, spherical, cylindrical, needle, pillow-block and precision bearings from leading global brands.",
    to: "/products/industrial-bearings",
  },
];

const INDUSTRIES = [
  ["Aerospace", Plane], ["Defence", ShieldCheck], ["Railways", Train],
  ["Power Generation", Zap], ["Heavy Engineering", Wrench], ["Oil & Gas", Droplets],
  ["Fabrication", Hammer], ["Steel", Layers], ["PEB", Building2],
  ["Galvanizing", Cog], ["Castings", Factory], ["Forgings", Hammer],
  ["Automotive", Cog], ["Manufacturing", Factory], ["Infrastructure", Building2],
  ["Wind Energy", Wind], ["Solar", Sun],
];

const CERT_PDF = "/certificates/3ak-authorised-distributor.pdf";
const CERT_IMG = "/certificates/3ak-thumb.jpg";
const HERO_IMAGE = "/products/hero-panels-4-final.jpg";

export default function AboutPage() {
  useSeo({
    title: "About Us — SSK Enterprises | Authorised Distributor of 3AK Chemie Pvt. Ltd.",
    description:
      "SSK Enterprises is the Authorised Distributor of 3AK Chemie Pvt. Ltd. for MR Chemie NDT products, KLYDE cold galvanizing zinc and industrial bearings — serving customers across Telangana and Andhra Pradesh.",
    path: "/about",
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About Us", path: "/about" }])],
  });

  const { openQuote } = useQuote();
  const [certOpen, setCertOpen] = useState(false);
  const [scale, setScale] = useState(1);

  return (
    <>
      {/* SECTION 1 — WHO WE ARE (two-column) */}
      <section className="bg-white" data-testid="about-who-we-are">
        <div className="container-x py-16 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-[3px] w-8 bg-[#C8102E]" />
              <span className="text-[11px] font-mono-tech tracking-[0.22em] uppercase font-bold text-[#0F2747]">Who We Are</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black text-[#0F2747] leading-[1.05] tracking-tight">About SSK Enterprises</h1>
            <p className="text-slate-600 mt-6 leading-relaxed text-[15.5px]">
              SSK Enterprises is the Authorised Distributor of 3AK Chemie Pvt. Ltd., supplying genuine MR Chemie NDT materials and inspection equipment, KLYDE cold galvanizing zinc products, BESSEY clamping and cutting tools, and industrial bearings across Telangana and Andhra Pradesh.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed text-[15.5px]">
              We support engineering, fabrication, maintenance, quality, procurement and industrial teams with genuine products, technical guidance, prompt quotations and consistent supply.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setCertOpen(true)} className="btn-accent" data-testid="about-view-cert">
                <Eye className="w-4 h-4" /> View Authorised Certificate
              </button>
              <Link to="/contact" className="btn-outline" data-testid="about-contact">Contact Us</Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <img
              src={HERO_IMAGE}
              alt="SSK Enterprises business divisions — MR Chemie NDT Products, KLYDE Cold Galvanizing Zinc, BESSEY Clamps, Industrial Bearings"
              loading="lazy"
              className="w-full h-auto shadow-md border border-slate-200"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHY CHOOSE SSK */}
      <section className="section section-alt" data-testid="about-why">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">02 · Why Choose SSK</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight mt-2">Why Choose SSK Enterprises</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY.map((w) => (
              <div key={w.title} className="bg-white border-l-4 border-[#C8102E] p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
                <w.icon className="w-8 h-8 text-[#0F2747]" strokeWidth={1.8} />
                <h3 className="font-display text-lg font-black text-[#0F2747] mt-4 leading-tight">{w.title}</h3>
                <p className="text-slate-600 mt-2 text-[14px] leading-relaxed">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — PRODUCTS WE SUPPLY */}
      <section className="section" data-testid="about-products">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">03 · Products We Supply</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight mt-2">Products We Supply</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {PRODUCTS.map((p) => (
              <Link key={p.title} to={p.to} className="group tech-card p-7 flex flex-col hover:border-[#C8102E] transition-colors" data-testid={`prod-${p.title.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
                <div className="w-12 h-12 flex items-center justify-center text-white" style={{ background: p.accent }}>
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl font-black text-[#0F2747] mt-5 leading-tight">{p.title}</h3>
                <p className="text-slate-600 mt-3 text-[14px] leading-relaxed flex-grow">{p.body}</p>
                <div className="inline-flex items-center gap-1 text-[#0F2747] font-semibold text-sm mt-5 group-hover:text-[#C8102E]">
                  Explore <ChevronRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — INDUSTRIES WE SERVE */}
      <section className="section section-alt" data-testid="about-industries">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">04 · Industries We Serve</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight mt-2">Industries We Serve</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {INDUSTRIES.map(([name, Ic]) => (
              <div key={name} className="bg-white border border-slate-200 hover:border-[#0F2747] p-4 flex flex-col items-center text-center gap-2.5" data-testid={`ind-${name.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
                <Ic className="w-6 h-6 text-[#C8102E]" strokeWidth={1.8} />
                <span className="text-[12px] font-semibold text-[#0F2747] leading-tight">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — MISSION & VISION (light, premium cards) */}
      <section className="section bg-white" data-testid="about-mission-vision">
        <div className="container-x">
          <div className="max-w-2xl mb-10">
            <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">05 · Mission & Vision</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight mt-2">Our Mission & Vision</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Mission Card */}
            <div className="bg-white border border-slate-200 rounded-xl p-8 md:p-10 shadow-sm hover:shadow-lg transition-shadow flex flex-col" data-testid="about-mission-card">
              <div className="w-14 h-14 rounded-lg bg-[#C8102E]/10 text-[#C8102E] flex items-center justify-center mb-6">
                <Wrench className="w-7 h-7" strokeWidth={2} />
              </div>
              <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">Our Mission</div>
              <h3 className="font-display text-2xl md:text-[26px] font-black text-[#0F2747] mt-3 leading-tight">Quality industrial products. Responsive technical support.</h3>
              <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
                To supply genuine, high-quality industrial products across NDT inspection, surface protection, clamping and cutting tools, and bearings while providing practical technical guidance, prompt quotations and consistent customer support.
              </p>
            </div>
            {/* Vision Card */}
            <div className="bg-white border border-slate-200 rounded-xl p-8 md:p-10 shadow-sm hover:shadow-lg transition-shadow flex flex-col" data-testid="about-vision-card">
              <div className="w-14 h-14 rounded-lg bg-[#C8102E]/10 text-[#C8102E] flex items-center justify-center mb-6">
                <Eye className="w-7 h-7" strokeWidth={2} />
              </div>
              <div className="text-[11px] font-mono-tech tracking-[0.22em] uppercase text-[#C8102E] font-bold">Our Vision</div>
              <h3 className="font-display text-2xl md:text-[26px] font-black text-[#0F2747] mt-3 leading-tight">To be a trusted industrial supply partner across Telangana and Andhra Pradesh.</h3>
              <p className="text-slate-600 mt-4 leading-relaxed text-[15px]">
                To build long-term partnerships with engineering and manufacturing industries by expanding our product capabilities, maintaining consistent supply and delivering responsive technical and commercial support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — PROFESSIONAL CTA */}
      <section className="section" data-testid="about-cta">
        <div className="container-x">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#0F2747] leading-tight">Need Technical Assistance?</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed text-[15px]">Our technical team is ready to help you choose the right products for your application.</p>
            <AboutCtaButtons openQuote={openQuote} />
          </div>
        </div>
      </section>

      {/* Certificate lightbox */}
      {certOpen && (
        <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4 md:p-8" onClick={() => setCertOpen(false)} role="dialog" aria-modal="true" data-testid="about-cert-lightbox">
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Zoom out"><ZoomOut className="w-4 h-4" /></button>
            <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Zoom in"><ZoomIn className="w-4 h-4" /></button>
            <a href={CERT_PDF} download className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" aria-label="Download"><Download className="w-4 h-4" /></a>
            <button onClick={() => setCertOpen(false)} className="w-10 h-10 bg-[#C8102E] hover:bg-[#8F0E15] text-white flex items-center justify-center" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>
          <div className="max-w-6xl w-full max-h-full overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img src={CERT_IMG} alt="SSK Enterprises Authorised Distributor Certificate" loading="lazy" decoding="async" style={{ transform: `scale(${scale})`, transformOrigin: "top center", transition: "transform 200ms ease" }} className="w-full h-auto shadow-2xl bg-white" />
          </div>
        </div>
      )}
    </>
  );
}

function AboutCtaButtons({ openQuote }) {
  return (
    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center max-w-2xl mx-auto">
      <button onClick={() => openQuote()} className="btn-accent flex-1" data-testid="cta-quote">
        Request a Quotation <ArrowRight className="w-4 h-4" />
      </button>
      <WhatsAppButton />
      <CallButton />
    </div>
  );
}

function WhatsAppButton() {
  // Uses the same settings context via a fallback (kept simple; QuoteProvider already picks WhatsApp number).
  const wa = "https://wa.me/918309352443?text=" + encodeURIComponent("Hello SSK Enterprises, I would like technical assistance.");
  return (
    <a href={wa} target="_blank" rel="noreferrer" className="btn-outline flex-1" data-testid="cta-wa">
      <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp
    </a>
  );
}

function CallButton() {
  return (
    <a href="tel:+919701766685" className="btn-outline flex-1" data-testid="cta-call">
      <Phone className="w-4 h-4" /> Call Now
    </a>
  );
}
